import { useNavigate } from 'react-router-dom';

import { RecordPageShell } from '~/components/layout';
import PersonaForm from '~/components/pages/ai/personas/PersonaForm';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import {
  emptyPersonaForm,
  type PersonaFormState,
  toCreatePersonaRequest,
} from '~/lib/ai/personaForm';
import { useCreatePersonaMutation } from '~/modules/query/ai/personas';
import { routes } from '~/app/routes';

export default function PersonaCreatePage() {
  const navigate = useNavigate();
  const [createPersona] = useCreatePersonaMutation();
  const { draft, patch, dirty } = useRecordDraft<null, PersonaFormState>(null, {
    id: 'new',
    toDraft: emptyPersonaForm,
  });
  const guard = useUnsavedGuard(dirty);

  const save = useRecordSave<PersonaFormState>({
    save: async (form) => {
      const created = await createPersona(toCreatePersonaRequest(form)).unwrap();
      guard.release();
      navigate(routes.aiPersona(created.data.id), { replace: true });
    },
    success: 'Persona created',
    failure: 'The persona could not be created. Try again.',
  });

  return (
    <RecordPageShell
      title="Create persona"
      breadcrumbs={[
        { label: 'AI' },
        { label: 'Personas', href: routes.aiPersonas },
        { label: 'Create persona' },
      ]}
      guard={guard}
    >
      {draft && (
        <PersonaForm
          draft={draft}
          patch={patch}
          dirty={dirty}
          busy={save.busy}
          error={save.error}
          onSubmit={() => save.submit(draft)}
        />
      )}
    </RecordPageShell>
  );
}
